import type { ReactNode } from "react";

interface RuntimeColorBadgeProps {
  children: ReactNode;
  color: string;
  "data-testid"?: string;
}

interface RuntimeColorDotLabelProps {
  children: ReactNode;
  color: string;
  "data-testid"?: string;
}

interface RuntimeColorContentProps {
  children: ReactNode;
  color: string;
}

interface RuntimeColorSwatchProps {
  color: string;
  size?: "compact" | "default";
}

/** Status badge for backend-defined colours that cannot be represented by a finite semantic tone. */
export function RuntimeColorBadge({ children, color, "data-testid": dataTestId }: RuntimeColorBadgeProps): ReactNode {
  return (
    <div className="flex size-fit max-w-full rounded-md bg-background-default">
      <span
        className="inline-flex size-fit min-h-badge-height-md max-w-full shrink items-center gap-xs overflow-hidden rounded-md px-badge-inline-md py-badge-block-md text-label-md"
        style={{ color, backgroundColor: `${color}1A` }}
        data-testid={dataTestId}
      >
        <span className="inline-flex min-w-0 items-center gap-xs truncate">{children}</span>
      </span>
    </div>
  );
}

/** Dot and label for backend-defined colours that cannot be represented by a finite semantic tone. */
export function RuntimeColorDotLabel({
  children,
  color,
  "data-testid": dataTestId,
}: RuntimeColorDotLabelProps): ReactNode {
  return (
    <span className="inline-flex items-center gap-space-sm" data-testid={dataTestId}>
      <span className="block size-status-dot shrink-0 rounded-full" style={{ backgroundColor: color }} />
      <span className="whitespace-nowrap">{children}</span>
    </span>
  );
}

/** Contains runtime text and icon colours supplied by project branding or backend data. */
export function RuntimeColorContent({ children, color }: RuntimeColorContentProps): ReactNode {
  return <span style={{ color }}>{children}</span>;
}

/** Square chart marker for runtime series colours. */
export function RuntimeColorSwatch({ color, size = "default" }: RuntimeColorSwatchProps): ReactNode {
  return (
    <span
      className={size === "compact" ? "block size-status-dot rounded-xs" : "block size-icon-inline rounded-xs"}
      style={{ backgroundColor: color }}
      aria-hidden="true"
    />
  );
}
